import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { catchError, map, throwError, timeout } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AuthService as SupplierAuthService } from '../services/v3/supplier/auth.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

/** No backend call should hang the UI forever (e.g. a stalled outbound MS Graph call during token validation). */
const REQUEST_TIMEOUT_MS = 30000;
export const SKIP_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const supplierAuthService = inject(SupplierAuthService)
  if (req.context.get(SKIP_INTERCEPTOR)) {
    return next(req);
  }

  if (req.url.includes('/api/supplier-portal')) {
    const supplierToken = localStorage.getItem('supplier_token') ?? '';
    const supplierId = supplierAuthService.authInfo()?.supplierId;
    const deviceKeyId = `firebase:supplier${supplierId}:device-id`;
    const deviceId = localStorage.getItem(deviceKeyId) || '';
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${supplierToken}`,
        'X-Supplier-Device-ID': deviceId
      }
    });
    return next(req).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      map(res => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status == 401) {
          window.location.href = '/supplier-login';
          supplierAuthService.removeAuth();
        }
        return throwError(()=> error);
      })
    );
  }

  // The self-registration flow (registration module) calls these public,
  // unauthenticated endpoints (routes/api.php - gated only by
  // 'allowed-web-domain', not 'ms-admin'). Without this exclusion an
  // anonymous visitor would get redirected to the staff login page below on
  // any 401, which shouldn't happen on these routes but would be a
  // confusing dead end for a candidate mid-registration if it ever did.
  const excludedUrls = ['/api/ecommerce/auth/validate-code', '/api/new-driver', '/api/state', '/api/vehicle-type', '/api/public-forms', '/api/public-abr', '/api/public-address', '/api/public/training'];
  const isExcluded = excludedUrls.some(url => req.url.includes(url));
  const authService = inject(AuthService);
  const router = inject(Router);
  if (isExcluded) {
    return next(req);
  }

  const authEcommerceApi = '/api/ecommerce';
  const requestUrl = req.url;
  if (requestUrl.includes(authEcommerceApi)) {
    const token = requestUrl.includes('auth/switch-panel-to-portal') ? localStorage.getItem('custm_token') : localStorage.getItem('ec_token');
    let headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    };

    //for track shipment null token and url track-my-freight will use different headers
    if (requestUrl.includes('ecommerce/track-my-freight') && !token) {
      headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }
    req = req.clone({
      setHeaders: headers
    });

    return next(req)
    .pipe(
      map(res => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status == 401) {
          localStorage.removeItem('ec_token');
          setTimeout(() => {
            window.location.href = '/ecommerce-login';
          }, 1500);
        }
        return throwError(()=> error);
      })
    )
  } else {
    let token = localStorage.getItem('custm_token');
    if (!authService.authCheck()) {
      token = localStorage.getItem('ec_token');
    }
    // No token yet (e.g. the initial login request) - omit the header
    // rather than send the literal string "Bearer null", which the backend's
    // global LogApiRequest middleware chokes on trying to JWT-decode.
    if (token) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }
    return next(req).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      map(res => {
        return res;
      }),
      catchError((error: HttpErrorResponse) => {
        const isLogin = req.params.get('isLogin');
        if (error.status == 401) {
          localStorage.removeItem('custm_token');
          localStorage.removeItem('user_details');
          if (!isLogin) {
            setTimeout(() => {
              const loginPath = !router.url.includes('/ecommerce') ? 'custm-login' : 'ecommerce-login';
              window.location.href = loginPath;
            }, 1500);
          }
        }
        return throwError(()=> error);
      })
    );
  }
}